import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  FaCloudUploadAlt,
  FaImages,
  FaSpinner,
  FaTrash,
  FaRedo,
  FaUndo,
} from "react-icons/fa";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import imageCompression from "browser-image-compression";
import Navbar from "./Navbar";
import "./GalleryPage.css";

const PAGE_SIZE = 60;

const GalleryPage = () => {
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [pendingActionId, setPendingActionId] = useState(null);

  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const sentinelRef = useRef(null);

  const isJallaAdmin =
    new URLSearchParams(window.location.search).get("jallaAdmin") === "1";

  const loadPage = useCallback(async (offset) => {
    const response = await fetch(
      `/api/photos?limit=${PAGE_SIZE}&offset=${offset}`
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || "Klarte ikke laste bilder");
    }
    return response.json();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError("");
    loadPage(0)
      .then((data) => {
        if (cancelled) return;
        setPhotos(data.photos || []);
        setTotal(data.total || 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    if (photos.length >= total) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          setIsLoading(true);
          loadPage(photos.length)
            .then((data) => {
              setPhotos((prev) => {
                const seen = new Set(prev.map((p) => p.id));
                const fresh = (data.photos || []).filter((p) => !seen.has(p.id));
                return [...prev, ...fresh];
              });
              setTotal(data.total || 0);
            })
            .catch((err) => setError(err.message))
            .finally(() => setIsLoading(false));
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [photos.length, total, isLoading, loadPage]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (files.length === 0) return;

    setError("");
    setIsUploading(true);
    setUploadQueue(
      files.map((f, i) => ({ id: i, name: f.name, status: "pending" }))
    );

    const uploadedPhotos = [];

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      setUploadQueue((prev) =>
        prev.map((q) => (q.id === i ? { ...q, status: "compressing" } : q))
      );

      let toUpload = file;
      try {
        toUpload = await imageCompression(file, {
          maxSizeMB: 4,
          maxWidthOrHeight: 3000,
          useWebWorker: true,
          initialQuality: 0.9,
        });
      } catch (compErr) {
        console.warn("Compression failed, uploading original:", compErr);
      }

      setUploadQueue((prev) =>
        prev.map((q) => (q.id === i ? { ...q, status: "uploading" } : q))
      );

      const formData = new FormData();
      formData.append("photos", toUpload, file.name);
      try {
        const response = await fetch("/api/photos", {
          method: "POST",
          body: formData,
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${response.status}`);
        }
        const data = await response.json();
        (data.photos || []).forEach((p) => {
          if (!p.error) uploadedPhotos.push(p);
        });
        setUploadQueue((prev) =>
          prev.map((q) => (q.id === i ? { ...q, status: "done" } : q))
        );
      } catch (err) {
        console.error("Upload error:", err);
        setUploadQueue((prev) =>
          prev.map((q) =>
            q.id === i ? { ...q, status: "error", error: err.message } : q
          )
        );
      }
    }

    if (uploadedPhotos.length > 0) {
      setPhotos((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const fresh = uploadedPhotos.filter((p) => !seen.has(p.id));
        return [...fresh, ...prev];
      });
      setTotal((prev) => prev + uploadedPhotos.length);
    }

    setIsUploading(false);
    setTimeout(() => setUploadQueue([]), 3000);
  };

  useEffect(() => {
    const node = dropZoneRef.current;
    if (!node) return undefined;
    const onDragOver = (e) => {
      e.preventDefault();
      node.classList.add("drag-over");
    };
    const onDragLeave = () => node.classList.remove("drag-over");
    const onDrop = (e) => {
      e.preventDefault();
      node.classList.remove("drag-over");
      if (e.dataTransfer && e.dataTransfer.files) {
        handleFiles(e.dataTransfer.files);
      }
    };
    node.addEventListener("dragover", onDragOver);
    node.addEventListener("dragleave", onDragLeave);
    node.addEventListener("drop", onDrop);
    return () => {
      node.removeEventListener("dragover", onDragOver);
      node.removeEventListener("dragleave", onDragLeave);
      node.removeEventListener("drop", onDrop);
    };
  });

  const handleDelete = async (photoId) => {
    if (!window.confirm("Slette dette bildet permanent?")) return;
    setPendingActionId(photoId);
    try {
      const response = await fetch(
        `/api/photos/${photoId}?jallaAdmin=1`,
        { method: "DELETE" }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Sletting feilet");
      }
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      setTotal((prev) => Math.max(0, prev - 1));
      if (lightboxIndex >= 0) setLightboxIndex(-1);
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingActionId(null);
    }
  };

  const handleRotate = async (photoId, delta) => {
    setPendingActionId(photoId);
    try {
      const response = await fetch(
        `/api/photos/${photoId}/rotate?jallaAdmin=1`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ delta }),
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Rotering feilet");
      }
      const data = await response.json();
      setPhotos((prev) =>
        prev.map((p) => (p.id === photoId ? data.photo : p))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingActionId(null);
    }
  };

  const slides = photos.map((p) => ({
    src: p.origUrl,
    width: p.width,
    height: p.height,
    srcSet: [
      { src: p.mediumUrl, width: Math.min(1400, p.width) },
      { src: p.origUrl, width: p.width },
    ],
  }));

  return (
    <div className="gallery-page">
      <Navbar />
      <header className="gallery-header">
        <h1>
          <FaImages /> Galleri
        </h1>
        <p className="gallery-subtitle">
          Del bilder fra dagen — last opp dine egne og se bilder fra andre
          gjester
        </p>
        {isJallaAdmin && <span className="admin-chip">jallaAdmin aktiv</span>}
      </header>

      <section className="gallery-upload" ref={dropZoneRef}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="upload-button"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={isUploading}
        >
          <FaCloudUploadAlt />
          <span>{isUploading ? "Laster opp..." : "Last opp bilder"}</span>
        </button>
        <p className="upload-hint">
          Eller dra og slipp bilder her. Du kan velge flere bilder samtidig.
        </p>

        {uploadQueue.length > 0 && (
          <ul className="upload-queue">
            {uploadQueue.map((q) => (
              <li key={q.id} className={`upload-item upload-${q.status}`}>
                <span className="upload-name">{q.name}</span>
                <span className="upload-status">
                  {q.status === "pending" && "Venter..."}
                  {q.status === "compressing" && (
                    <>
                      <FaSpinner className="spin" /> Klargjør
                    </>
                  )}
                  {q.status === "uploading" && (
                    <>
                      <FaSpinner className="spin" /> Laster opp
                    </>
                  )}
                  {q.status === "done" && "Ferdig"}
                  {q.status === "error" && (q.error || "Feil")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {error && (
        <div className="gallery-error" role="alert">
          {error}
        </div>
      )}

      {photos.length === 0 && !isLoading && !error && (
        <div className="gallery-empty">
          <FaImages />
          <p>Ingen bilder ennå. Vær den første som deler!</p>
        </div>
      )}

      <div className="gallery-grid">
        {photos.map((photo, index) => (
          <motion.div
            key={photo.id}
            className="gallery-item"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            style={{
              aspectRatio: `${photo.width} / ${photo.height}`,
            }}
          >
            <img
              src={photo.thumbUrl}
              alt=""
              loading="lazy"
              onClick={() => setLightboxIndex(index)}
            />
            {isJallaAdmin && (
              <div className="admin-controls">
                <button
                  type="button"
                  title="Roter mot venstre"
                  onClick={() => handleRotate(photo.id, -90)}
                  disabled={pendingActionId === photo.id}
                >
                  <FaUndo />
                </button>
                <button
                  type="button"
                  title="Roter mot høyre"
                  onClick={() => handleRotate(photo.id, 90)}
                  disabled={pendingActionId === photo.id}
                >
                  <FaRedo />
                </button>
                <button
                  type="button"
                  title="Slett"
                  className="danger"
                  onClick={() => handleDelete(photo.id)}
                  disabled={pendingActionId === photo.id}
                >
                  <FaTrash />
                </button>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      <div ref={sentinelRef} className="gallery-sentinel">
        {photos.length < total && isLoading && (
          <FaSpinner className="spin" />
        )}
      </div>

      <Lightbox
        open={lightboxIndex >= 0}
        index={lightboxIndex < 0 ? 0 : lightboxIndex}
        close={() => setLightboxIndex(-1)}
        slides={slides}
        plugins={[Zoom, Counter]}
        zoom={{ maxZoomPixelRatio: 4, scrollToZoom: true }}
        carousel={{ finite: true }}
        controller={{ closeOnBackdropClick: true }}
      />
    </div>
  );
};

export default GalleryPage;
